// CPLEX Service — admin model registry + runs
//
// Mirrors app/routes/admin_cplex.py. All endpoints are gated by admin:system
// scope server-side; the UI just hides the menu link for non-admins.

import api from './api';
import { triggerBlobDownload } from '../utils/triggerDownload';

// ---------------------------------------------------------------------------
// Types — mirrors app/schemas/cplex.py
// ---------------------------------------------------------------------------

export type CplexRunState =
  | 'queued'
  | 'uploading'
  | 'dispatching'
  | 'solving'
  | 'optimal'
  | 'infeasible'
  | 'error';

export const TERMINAL_STATES: ReadonlySet<CplexRunState> = new Set([
  'optimal',
  'infeasible',
  'error',
]);

export interface FileManifestEntry {
  name: string;
  size_bytes: number;
  sha256: string;
}

export interface CplexModelVersion {
  id: string;
  model_id: string;
  version: number;
  workspace_sha256: string;
  entrypoint: string;
  file_manifest: FileManifestEntry[];
  notes: string | null;
  uploaded_by: string | null;
  uploaded_at: string;
}

export interface CplexModel {
  id: string;
  name: string;
  notes: string | null;
  current_version: number;
  is_active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

export interface CplexModelDetail extends CplexModel {
  versions: CplexModelVersion[];
}

// Daily-View enrichment side maps (populated only when getRun passes ?enrich=).
// Mirrors OpenFillTicket / OpenFillTicketSummary in app/schemas/cplex.py.
export interface OpenFillTicket {
  ticket: number;
  status: string;
  size: number | null;
  planned_date: string | null;
}
export interface OpenFillTicketSummary {
  item_code: string;
  count: number;
  tickets: OpenFillTicket[];
}
// order_scores keyed by the MPP `order` string ("Customer Name (AU#####)");
// open_fill_tickets keyed by Sage item_code.
export type OrderScoreMap = Record<string, number>;
export type OpenFillTicketMap = Record<string, OpenFillTicketSummary>;

export interface CplexRun {
  id: string;
  model_id: string;
  model_version: number;
  actor_user_id: string | null;
  state: CplexRunState;
  result_summary: Record<string, unknown> | null;
  result_tables: Record<string, unknown> | null;
  error_message: string | null;
  created_at: string;
  finished_at: string | null;
  // Optional Daily-View enrichments — present only when fetched with enrich=.
  order_scores?: OrderScoreMap | null;
  open_fill_tickets?: OpenFillTicketMap | null;
}

export interface CplexRunSummary {
  id: string;
  model_id: string;
  model_name: string | null;
  model_version: number;
  state: CplexRunState;
  objective: number | null;
  solve_seconds: number | null;
  error_message: string | null;
  created_at: string;
  finished_at: string | null;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

const BASE = '/admin/cplex';

// ---------------------------------------------------------------------------
// Input refresh (Sources tab)
// ---------------------------------------------------------------------------

export type CplexInputBatchState =
  | 'pending'
  | 'fetching'
  | 'unchanged'
  | 'promoted'
  | 'aborted';

export type CplexInputFetchState =
  | 'pending'
  | 'fetched'
  | 'empty'
  | 'http_error'
  | 'parse_failed';

export interface CplexInputFetchRow {
  filename: string;
  remote_name: string;
  state: CplexInputFetchState;
  http_status: number | null;
  size_bytes: number | null;
  response_sha256: string | null;
  parse_error: string | null;
  row_count: number | null;
  fetched_at: string;
}

export interface CplexInputBatch {
  id: string;
  latest_id: string | null;
  previous_latest_id: string | null;
  state: CplexInputBatchState;
  trigger: string;
  promoted_version_id: string | null;
  error_message: string | null;
  started_at: string;
  finished_at: string | null;
  fetched_count: number;
  failed_count: number;
}

export interface CplexInputBatchDetail extends CplexInputBatch {
  fetches: CplexInputFetchRow[];
}

export interface CplexInputForceRefreshResult {
  enqueued: boolean;
  task_id: string | null;
  reason: string | null;
}

export async function getLatestInputBatch(
  modelId: string,
): Promise<CplexInputBatchDetail | null> {
  const { data } = await api.get(
    `${BASE}/models/${modelId}/inputs/batches/latest`,
  );
  return data ?? null;
}

export async function listInputBatches(
  modelId: string,
  opts: { limit?: number; offset?: number } = {},
): Promise<{ items: CplexInputBatch[]; total: number }> {
  const { data } = await api.get(`${BASE}/models/${modelId}/inputs/batches`, {
    params: opts,
  });
  return data;
}

export async function forceInputRefresh(
  modelId: string,
): Promise<CplexInputForceRefreshResult> {
  const { data } = await api.post(
    `${BASE}/models/${modelId}/inputs/refresh`,
  );
  return data;
}

export async function listModels(includeArchived = false): Promise<CplexModel[]> {
  const { data } = await api.get(`${BASE}/models`, {
    params: { include_archived: includeArchived },
  });
  return data.items;
}

export async function getModel(modelId: string): Promise<CplexModelDetail> {
  const { data } = await api.get(`${BASE}/models/${modelId}`);
  return data;
}

export async function createModel(args: {
  name: string;
  notes?: string;
  entrypoint?: string;
  files: File[];
}): Promise<CplexModelDetail> {
  const form = new FormData();
  form.append('name', args.name);
  if (args.notes) form.append('notes', args.notes);
  if (args.entrypoint) form.append('entrypoint', args.entrypoint);
  for (const f of args.files) form.append('files', f, f.name);
  const { data } = await api.post(`${BASE}/models`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export async function updateModel(
  modelId: string,
  patch: { name?: string; notes?: string; is_active?: boolean },
): Promise<CplexModel> {
  const { data } = await api.patch(`${BASE}/models/${modelId}`, patch);
  return data;
}

export async function archiveModel(modelId: string): Promise<CplexModel> {
  const { data } = await api.post(`${BASE}/models/${modelId}/archive`);
  return data;
}

export async function uploadVersion(
  modelId: string,
  args: { notes?: string; entrypoint?: string; files: File[] },
): Promise<CplexModelVersion> {
  const form = new FormData();
  if (args.notes) form.append('notes', args.notes);
  if (args.entrypoint) form.append('entrypoint', args.entrypoint);
  for (const f of args.files) form.append('files', f, f.name);
  const { data } = await api.post(`${BASE}/models/${modelId}/versions`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/**
 * Notebook-mode upload: PJ ships a single .ipynb or .py and we handle the
 * tarball assembly + CSV carry-forward server-side. Backed by
 * POST /admin/cplex/models/{id}/notebook (services/cplex/notebook_upload.py).
 */
export async function uploadNotebookVersion(
  modelId: string,
  args: { notebook: File; notes?: string },
): Promise<CplexModelVersion> {
  const form = new FormData();
  form.append('notebook', args.notebook, args.notebook.name);
  if (args.notes) form.append('notes', args.notes);
  const { data } = await api.post(
    `${BASE}/models/${modelId}/notebook`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

/**
 * PJ-facing notebook upload guide (markdown). Backed by
 * GET /admin/cplex/notebook-upload-guide. Lives in the repo at
 * docs/integrations/pj-supply-chain-notebook-author.md and is served
 * verbatim so the doc is single-sourced.
 */
export async function getNotebookUploadGuide(): Promise<string> {
  const { data } = await api.get(`${BASE}/notebook-upload-guide`, {
    responseType: 'text',
    transformResponse: [(d) => d],  // axios JSON-parses by default; opt out
  });
  return data as string;
}

export async function startRun(
  modelId: string,
  clientRunId?: string,
): Promise<CplexRun> {
  const form = new FormData();
  form.append('model_id', modelId);
  if (clientRunId) form.append('client_run_id', clientRunId);
  const { data } = await api.post(`${BASE}/runs`, form);
  return data;
}

// `tables` (comma-separated) requests a LEAN payload: the backend filters
// result_tables to just those keys and skips the large, unused vm_response_body.
// `modelType` (e.g. '5_day') further scopes master_production_plan to one solver
// variant — the daily Sankey page renders one model at a time, but the MPP table
// carries all four (~3.3x the rows shown), so scoping cuts what the client must
// decode, parse, and build. The full-bleed Sankey page passes both.
export async function getRun(
  runId: string,
  tables?: string,
  modelType?: string,
  // `enrich` (comma list, e.g. 'scores,fill_tickets') attaches the Daily-View
  // side maps order_scores + open_fill_tickets. Best-effort, off by default.
  enrich?: string,
): Promise<CplexRun> {
  const params: Record<string, string> = {};
  if (tables) params.tables = tables;
  if (modelType) params.model_type = modelType;
  if (enrich) params.enrich = enrich;
  const { data } = await api.get(`${BASE}/runs/${runId}`, {
    params: Object.keys(params).length ? params : undefined,
  });
  return data;
}

export async function listRuns(
  modelId?: string,
  limit = 50,
  offset = 0,
): Promise<{ items: CplexRunSummary[]; total: number }> {
  const { data } = await api.get(`${BASE}/runs`, {
    params: { model_id: modelId, limit, offset },
  });
  return data;
}

// ---------------------------------------------------------------------------
// Model-Version Agent (v2) — version lineage (mirrors admin_cplex_versions.py)
// ---------------------------------------------------------------------------

export interface VersionNodeSummary {
  id: string;
  cplex_model_id: string;
  parent_version_id: string | null;
  label: string;
  state: string;
  dynamic_metadata: Record<string, unknown>;
  summary_deltas: Record<string, unknown> | null;
  created_at: string;
}

export interface VersionNodeDetail extends VersionNodeSummary {
  workspace_blob_uri: string | null;
  user_prompt: string | null;
  result_summary: Record<string, unknown> | null;
  result_tables: Record<string, unknown> | null;
}

export async function listVersionNodes(
  modelId: string,
): Promise<VersionNodeSummary[]> {
  const { data } = await api.get(`${BASE}/models/${modelId}/version-nodes`);
  return (data.items ?? []) as VersionNodeSummary[];
}

export async function getVersionNode(
  versionId: string,
): Promise<VersionNodeDetail> {
  const { data } = await api.get(`${BASE}/version-nodes/${versionId}`);
  return data as VersionNodeDetail;
}

export interface WorkspaceFileContent {
  name: string;
  encoding: 'utf-8' | 'base64';
  content: string;
  size_bytes: number;
  truncated: boolean;
}

export async function getWorkspaceDownloadUrl(
  modelId: string,
  version: number,
): Promise<{ download_url: string; expires_in: number; filename: string }> {
  const { data } = await api.get(
    `${BASE}/models/${modelId}/versions/${version}/workspace-url`,
  );
  return data;
}

export async function getWorkspaceFile(
  modelId: string,
  version: number,
  name: string,
): Promise<WorkspaceFileContent> {
  const { data } = await api.get(
    `${BASE}/models/${modelId}/versions/${version}/files`,
    { params: { name } },
  );
  return data;
}

/**
 * Download a single run's full input + output set as a zip. Triggers a
 * browser save dialog by creating an object URL from the blob and
 * clicking a detached synthetic anchor (see utils/triggerDownload).
 */
export async function downloadRunArchive(runId: string): Promise<void> {
  const response = await api.get(`${BASE}/runs/${runId}/download`, {
    responseType: 'blob',
  });
  const blob = response.data as Blob;
  const url = URL.createObjectURL(blob);
  triggerBlobDownload(url, `cplex_run_${runId}.zip`);
  URL.revokeObjectURL(url);
}
